import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";

function variantPrice(product, v) {
  if (v?.sale_price != null && v?.sale_price !== "") return Number(v.sale_price);
  if (v?.price != null && v?.price !== "") return Number(v.price);
  if (product.sale_price != null && product.sale_price !== "") return Number(product.sale_price);
  return Number(product.price);
}

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { currency } = useSettings();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [selVariant, setSelVariant] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      if ((r.data.variants || []).length > 0) setSelVariant(r.data.variants[0]);
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-display font-bold text-[#1A231C]">Product not found</h1>
        <Link to="/shop" className="inline-flex items-center gap-1 mt-4 text-[var(--brand-primary)] font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to shop
        </Link>
      </div>
    );
  }
  if (!product) return <div className="py-24 text-center text-[#5C665D]">Loading...</div>;

  const gallery = product.images && product.images.length ? product.images : [product.image_url].filter(Boolean);
  const hasVariants = (product.variants || []).length > 0;
  const price = variantPrice(product, hasVariants ? selVariant : null);
  const basePrice = hasVariants ? (selVariant?.price ?? product.price) : product.price;
  const onSale = hasVariants
    ? (selVariant?.sale_price != null && selVariant?.sale_price !== "" && Number(selVariant.sale_price) < Number(basePrice))
    : (product.sale_price != null && product.sale_price !== "" && Number(product.sale_price) < Number(product.price));
  const available = hasVariants ? (selVariant?.stock ?? 0) : (product.stock ?? 0);

  const handleAdd = () => {
    addItem(product, hasVariants ? selVariant : null);
    toast.success(`${product.name} added to cart`);
  };

  const mainImg = (hasVariants && selVariant?.image_url) ? selVariant.image_url : (gallery[activeImg] || product.image_url);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/shop" className="inline-flex items-center gap-1 text-[#5C665D] hover:text-[#1A231C] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to shop
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-[#F4EFE6] border border-[#E8E4D9]">
            <img src={mainImg} alt={product.name} data-testid="detail-main-image" className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-4">
              {gallery.map((img, idx) => (
                <button
                  key={idx}
                  data-testid={`detail-thumb-${idx}`}
                  onClick={() => setActiveImg(idx)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${activeImg === idx ? "border-[var(--brand-primary)]" : "border-[#E8E4D9]"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1A231C] font-display">{product.name}</h1>
          {product.unit && <p className="text-[#5C665D] mt-1">{product.unit}</p>}
          <div className="flex items-baseline gap-3 mt-4">
            {onSale ? (
              <>
                <span className="text-3xl font-display font-bold text-[var(--brand-accent)]">{currency}{price}</span>
                <span className="text-lg text-[#9CA3AF] line-through">{currency}{basePrice}</span>
              </>
            ) : (
              <span className="text-3xl font-display font-bold text-[#1A231C]">{currency}{price}</span>
            )}
          </div>

          <p className="text-[#5C665D] leading-relaxed mt-5 whitespace-pre-line">{product.description}</p>

          {hasVariants && (
            <div className="mt-6">
              <p className="text-sm font-medium text-[#1A231C] mb-2">Choose an option</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => {
                  const label = [v.size, v.color, v.pack].filter((x) => x && x !== "Default").join(" / ") || v.sku || "Option";
                  const sel = selVariant?.id === v.id;
                  const out = (v.stock ?? 0) <= 0;
                  return (
                    <button
                      key={v.id}
                      data-testid={`variant-${v.id}`}
                      disabled={out}
                      onClick={() => setSelVariant(v)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40 disabled:line-through ${
                        sel ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-[#1A231C] border-[#E8E4D9] hover:border-[var(--brand-primary)]"
                      }`}
                    >
                      {sel && <Check className="w-3.5 h-3.5" />}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <p className="mt-4 text-sm" data-testid="detail-stock">
            {available > 0 ? (
              <span className="text-[var(--brand-primary)] font-medium">{available} in stock</span>
            ) : (
              <span className="text-[var(--brand-accent)] font-medium">Out of stock</span>
            )}
          </p>

          <button
            data-testid="detail-add-to-cart"
            onClick={handleAdd}
            disabled={available <= 0 || !product.in_stock}
            className="mt-6 inline-flex items-center gap-2 bg-[var(--brand-primary)] text-white rounded-lg px-8 py-3.5 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" /> Add to cart
          </button>
        </div>
      </div>
    </div>
  );
}
